import { useState, useMemo, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ModelWithFilesAndLinks } from '@maker-toolchest/shared';
import { Box, Tag, User, File, Link as LinkIcon, Folder } from 'lucide-react';
import { ModelFilesModal } from './ModelFilesModal';
import { ModelLinksModal } from './ModelLinksModal';
import { ModelCollectionsModal } from './ModelCollectionsModal';
import { LinkBadge } from '@/components/LinkBadge';
import { getRelativeTime } from '@/utils/formatting';

interface ModelCardProps {
  model: ModelWithFilesAndLinks;
  variant?: 'default' | 'compact';
  showRelativeTime?: boolean;
}

// Memoize card component to prevent unnecessary re-renders when other cards change
function ModelCardComponent({ model, variant = 'default', showRelativeTime = false }: ModelCardProps) {
  const navigate = useNavigate();
  const [showFilesModal, setShowFilesModal] = useState(false);
  const [showLinksModal, setShowLinksModal] = useState(false);
  const [showCollectionsModal, setShowCollectionsModal] = useState(false);

  const handleCardClick = () => {
    navigate(`/models/${model.id}`);
  };

  const handleBadgeClick = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation(); // Prevent card navigation
    action();
  };

  // Get unique sources from links (memoized to avoid recreation on every render)
  const uniqueSources = useMemo(
    () => [...new Set(model.links.map((l) => l.source))],
    [model.links]
  );

  // Get the most recent interaction timestamp for relative time (memoized to avoid expensive recalculation)
  const relativeTime = useMemo(() => {
    if (!showRelativeTime) return null;

    const timestamps = [
      model.lastViewed,
      model.lastPrinted,
      model.addedAt,
      model.updatedAt,
    ].filter((t): t is Date => t !== null).map(t => new Date(t));

    const mostRecent = timestamps.length === 0
      ? new Date(model.createdAt)
      : new Date(Math.max(...timestamps.map(t => t.getTime())));

    return getRelativeTime(mostRecent);
  }, [showRelativeTime, model.lastViewed, model.lastPrinted, model.addedAt, model.updatedAt, model.createdAt]);

  // Thumbnail logic - ModelFile doesn't have thumbnailUrl, use model's thumbnailUrl
  const thumbnail = model.thumbnailUrl;

  // Shared badge components
  const filesBadge = model.files.length > 0 && (
    <button
      onClick={(e) => handleBadgeClick(e, () => setShowFilesModal(true))}
      className={`flex items-center gap-1 ${
        variant === 'compact'
          ? 'px-1.5 py-0.5 text-xs'
          : 'px-2 py-1 text-xs'
      } font-medium rounded bg-primary/10 text-primary hover:bg-primary/20 transition-colors`}
    >
      <File className="h-3 w-3" />
      <span>
        {model.files.length} {model.files.length === 1 ? 'file' : 'files'}
      </span>
    </button>
  );

  const linksBadge = model.links.length > 0 && (
    <button
      onClick={(e) => handleBadgeClick(e, () => setShowLinksModal(true))}
      className={`flex items-center gap-1 ${
        variant === 'compact'
          ? 'px-1.5 py-0.5 text-xs'
          : 'px-2 py-1 text-xs'
      } font-medium rounded bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/40 transition-colors`}
    >
      <LinkIcon className="h-3 w-3" />
      <span>
        {model.links.length} {model.links.length === 1 ? 'link' : 'links'}
      </span>
    </button>
  );

  const thumbnailElement = (
    <div className={`flex items-center justify-center bg-background-subtle ${
      variant === 'compact'
        ? 'h-16 w-16 flex-shrink-0 rounded'
        : 'aspect-video'
    }`}>
      {thumbnail ? (
        <img
          src={thumbnail}
          alt={model.name}
          className="w-full h-full object-cover"
        />
      ) : (
        <Box className={variant === 'compact' ? 'h-8 w-8' : 'h-16 w-16'} />
      )}
    </div>
  );

  const modals = (
    <>
      <ModelFilesModal
        open={showFilesModal}
        onClose={() => setShowFilesModal(false)}
        modelId={model.id}
        modelName={model.name}
        files={model.files}
      />
      <ModelLinksModal
        open={showLinksModal}
        onClose={() => setShowLinksModal(false)}
        modelName={model.name}
        links={model.links}
      />
      <ModelCollectionsModal
        open={showCollectionsModal}
        onClose={() => setShowCollectionsModal(false)}
        modelName={model.name}
        collections={model.collections || []}
      />
    </>
  );

  // Compact variant - horizontal layout
  if (variant === 'compact') {
    return (
      <>
        <div
          onClick={handleCardClick}
          className="flex cursor-pointer items-center gap-3 rounded border border-border bg-background p-3 transition-colors hover:bg-background-subtle"
        >
          {thumbnailElement}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-foreground">{model.name}</p>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              {relativeTime && (
                <p className="text-xs text-foreground-muted">{relativeTime}</p>
              )}
              {filesBadge}
              {linksBadge}
            </div>
          </div>
        </div>
        {modals}
      </>
    );
  }

  // Default variant - full card for grid view
  return (
    <>
      <div
        onClick={handleCardClick}
        className="bg-background rounded-lg border border-border overflow-hidden hover:border-border-hover transition-colors cursor-pointer"
      >
        {/* Thumbnail */}
        {thumbnailElement}

        {/* Content */}
        <div className="p-4 space-y-3">
          {/* Header */}
          <div className="space-y-2">
            <h3 className="font-semibold text-foreground line-clamp-2">{model.name}</h3>

            {model.description && (
              <p className="text-sm text-foreground-muted line-clamp-2">{model.description}</p>
            )}
          </div>

          {/* Files and Links Badges */}
          <div className="flex flex-wrap gap-2">
            {filesBadge}
            {linksBadge}
          </div>

          {/* Collections Summary */}
          {model.collections && model.collections.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {model.collections.length === 1 ? (
                // Single collection - show colored pill with collection name
                <button
                  onClick={(e) => handleBadgeClick(e, () => setShowCollectionsModal(true))}
                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded border transition-opacity hover:opacity-80"
                  style={{
                    borderColor: model.collections[0].collection.color || '#e5e7eb',
                    color: model.collections[0].collection.color || '#6b7280',
                    backgroundColor: model.collections[0].collection.color
                      ? `${model.collections[0].collection.color}10`
                      : '#f3f4f6',
                  }}
                >
                  <Folder className="h-3 w-3" />
                  <span>{model.collections[0].collection.name}</span>
                </button>
              ) : (
                // Multiple collections - show gray pill with count
                <button
                  onClick={(e) => handleBadgeClick(e, () => setShowCollectionsModal(true))}
                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-800 dark:bg-gray-800/30 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800/40 transition-colors"
                >
                  <Folder className="h-3 w-3" />
                  <span>{model.collections.length} Collections</span>
                </button>
              )}
            </div>
          )}

          {/* Source Badges (from links) */}
          {uniqueSources.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {uniqueSources.map((source) => (
                <LinkBadge key={source} source={source} className="py-0.5" />
              ))}
            </div>
          )}

          {/* Metadata */}
          {model.author && (
            <div className="flex items-center gap-1 text-xs text-foreground-muted">
              <User className="h-4 w-4" />
              <span>{model.author}</span>
            </div>
          )}

          {/* Tags */}
          {model.tags && model.tags.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <Tag className="h-4 w-4 text-foreground-muted" />
              {model.tags.slice(0, 3).map((tag, idx) => (
                <span
                  key={idx}
                  className="px-2 py-0.5 text-xs bg-background-subtle text-foreground-muted rounded"
                >
                  {tag}
                </span>
              ))}
              {model.tags.length > 3 && (
                <span className="text-xs text-foreground-muted">+{model.tags.length - 3} more</span>
              )}
            </div>
          )}

          {/* License */}
          {model.license && (
            <div className="text-xs text-foreground-muted">License: {model.license}</div>
          )}
        </div>
      </div>

      {modals}
    </>
  );
}

// Export memoized version
export const ModelCard = memo(ModelCardComponent);
